import {
  Button,
  FlexBox,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import CategoryTreeInput from 'lib-knowledge-management-common/components/categoryTreeInput/index';
import { LanguageContext } from 'lib-knowledge-management-common/contexts/language';
import {
  CategoryQuery,
  useCategoryQuery,
} from 'lib-knowledge-management-common/graphql';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { collectionIdAtom } from 'models/collectionStore';
import { useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { CategoryEditModalLoadingPlaceholder } from './categoryEditModalLoadingPlaceholder';
import {
  CategoryEditModalSchema,
  categoryEditModalSchema,
} from './categoryEditModalSchema';
import { MultiLingualCategoryNames } from './multiLingualCategoryNames';
import { useCategoryUpdate } from './useCategoryUpdate';

type CategoryEditModalProps = {
  categoryId: string;
};
export function CategoryEditModal({
  categoryId,
}: CategoryEditModalProps): JSX.Element {
  const { data: categoryData, loading: categoryLoading } = useCategoryQuery({
    variables: {
      id: categoryId,
    },
  });

  if (categoryLoading || !categoryData) {
    return (
      <ModalWrapper>
        <CategoryEditModalLoadingPlaceholder />
      </ModalWrapper>
    );
  }

  return <CategoryEditModalContent category={categoryData.category} />;
}

type CategoryEditModalContentProps = {
  category: CategoryQuery['category'];
};

function CategoryEditModalContent({ category }: CategoryEditModalContentProps) {
  const { hideModal } = useModal();
  const { successNotify, errorNotify } = useNotification();
  const collectionId = useAtomValue(collectionIdAtom);
  const { enabledLanguages } = useContext(LanguageContext);

  const { control, setValue, watch, formState, handleSubmit } =
    useForm<CategoryEditModalSchema>({
      resolver: zodResolver(categoryEditModalSchema),
      defaultValues: {
        parentRef: {
          __typename:
            category?.parent?.__typename === 'Category'
              ? 'Category'
              : 'Collection',
          id: category?.parent?.id || '',
        },
        titles:
          enabledLanguages.map((t) => ({
            languageId: t.languageId,
            title:
              category?.titles.find(
                (title) => title.languageId === t.languageId,
              )?.title || undefined,
          })) || [],
      },
      reValidateMode: 'onChange',
      mode: 'all',
    });

  const parentRef = watch('parentRef');

  const [onSubmit, { saveDisabled }] = useCategoryUpdate({
    categoryId: category?.id,
    parentRef,
    formState: formState,
    onCompleted: () => {
      const defaultLanguageTitle = category?.titles.find(
        (title) => title.languageId === enabledLanguages[0].languageId,
      )?.title;
      const firstTitle = category?.titles[0]?.title;
      const titleToShow = defaultLanguageTitle || firstTitle || '';

      hideModal();
      successNotify({
        title: 'Success',
        text: `Category ${titleToShow ? `"${titleToShow}"` : ''} has been updated`,
      });
    },
    onMoveError: (err) => {
      errorNotify({
        text: `Failed to move category, please refresh and try again`,
      });
      throw err;
    },
    onTitleUpsertError: (err) => {
      errorNotify({
        text: 'Failed to update category title(s), please refresh and try again',
      });
      throw err;
    },
    onTitleDeleteError: (err) => {
      errorNotify({
        text: 'Failed to delete category title(s), please refresh and try again',
      });
      throw err;
    },
  });

  if (!category) return <></>;

  return (
    <ModalWrapper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHead title="Edit category" onClose={hideModal}></ModalHead>
        <ModalBody>
          <MultiLingualCategoryNames
            control={control}
            enabledLanguages={enabledLanguages}
          />
          <Controller
            control={control}
            name="parentRef"
            render={() => (
              <CategoryTreeInput
                withCollectionOption
                value={parentRef}
                onChange={(value) => {
                  setValue('parentRef', value, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                currentCategoryId={category.id}
                collectionId={collectionId}
              />
            )}
          />
        </ModalBody>
        <ModalFoot>
          <FlexBox justifyContent="flex-end" gap="100">
            <Button variant="tertiary" onClick={hideModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveDisabled}>
              Save
            </Button>
          </FlexBox>
        </ModalFoot>
      </form>
    </ModalWrapper>
  );
}
